import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { Input } from "../../components/ui/Input";
import { Text } from "../../components/ui/Text";
import { api } from "../../services/api";
import { FaPlus, FaTrash, FaEdit } from "react-icons/fa";

interface Contributor {
  _id: string;
  name: string;
  email?: string;
  isActive: boolean;
}

interface Role {
  _id: string;
  name: string;
  description?: string;
  isActive: boolean;
}

type TabType = "contributors" | "roles" | "category-rules";

export const ProfitRulesSettings: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>("roles");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Contributors state
  const [contributors, setContributors] = useState<Contributor[]>([]);
  const [contributorForm, setContributorForm] = useState({ name: "", email: "" });
  const [editingContributor, setEditingContributor] = useState<string | null>(null);

  // Roles state
  const [roles, setRoles] = useState<Role[]>([]);
  const [roleForm, setRoleForm] = useState({ name: "", description: "" });
  const [editingRole, setEditingRole] = useState<string | null>(null);

  // Category Profit Rules state
  const [categories, setCategories] = useState<any[]>([]);
  const [categoryRules, setCategoryRules] = useState<Record<string, any>>({});

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === "contributors") {
        const response = await api.get("/profit-rules/contributors");
        setContributors(response.data);
      } else if (activeTab === "roles") {
        const response = await api.get("/profit-rules/roles");
        setRoles(response.data);
      } else if (activeTab === "category-rules") {
        const categoriesResponse = await api.get("/categories?includeInactive=true");
        const categoriesList = categoriesResponse.data.categories || [];
        setCategories(categoriesList);
        
        // Load profit rules for all categories
        const rulesMap: Record<string, any> = {};
        await Promise.all(
          categoriesList.map(async (cat: any) => {
            try {
              const rulesResponse = await api.get(`/profit-rules/categories/${cat._id}/rules`);
              if (rulesResponse.data) {
                rulesMap[cat._id] = rulesResponse.data;
              }
            } catch (err: any) {
              // 404 is fine - no rules exist
              if (err.response?.status !== 404) {
                console.error(`Failed to load rules for category ${cat._id}:`, err);
              }
            }
          })
        );
        setCategoryRules(rulesMap);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.profitRulesSettings.errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContributor = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await api.post("/profit-rules/contributors", contributorForm);
      setContributors([...contributors, response.data]);
      setContributorForm({ name: "", email: "" });
      setSuccess(t("admin.profitRulesSettings.success.contributorCreated"));
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.profitRulesSettings.errors.createContributorFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateContributor = async (id: string) => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const contributor = contributors.find((c) => c._id === id);
      if (!contributor) return;

      const response = await api.put(`/profit-rules/contributors/${id}`, {
        name: contributorForm.name || contributor.name,
        email: contributorForm.email || contributor.email,
      });
      setContributors(contributors.map((c) => (c._id === id ? response.data : c)));
      setEditingContributor(null);
      setContributorForm({ name: "", email: "" });
      setSuccess(t("admin.profitRulesSettings.success.contributorUpdated"));
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.profitRulesSettings.errors.updateContributorFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContributor = async (id: string) => {
    if (!window.confirm(t("admin.profitRulesSettings.confirm.deleteContributor"))) return;

    setSaving(true);
    setError(null);
    try {
      await api.delete(`/profit-rules/contributors/${id}`);
      setContributors(contributors.filter((c) => c._id !== id));
      setSuccess(t("admin.profitRulesSettings.success.contributorDeleted"));
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.profitRulesSettings.errors.deleteContributorFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await api.post("/profit-rules/roles", roleForm);
      setRoles([...roles, response.data]);
      setRoleForm({ name: "", description: "" });
      setSuccess(t("admin.profitRulesSettings.success.roleCreated"));
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.profitRulesSettings.errors.createRoleFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRole = async (id: string) => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const role = roles.find((r) => r._id === id);
      if (!role) return;

      const response = await api.put(`/profit-rules/roles/${id}`, {
        name: roleForm.name || role.name,
        description: roleForm.description || role.description,
      });
      setRoles(roles.map((r) => (r._id === id ? response.data : r)));
      setEditingRole(null);
      setRoleForm({ name: "", description: "" });
      setSuccess(t("admin.profitRulesSettings.success.roleUpdated"));
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.profitRulesSettings.errors.updateRoleFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async (id: string) => {
    if (!window.confirm(t("admin.profitRulesSettings.confirm.deleteRole"))) return;

    setSaving(true);
    setError(null);
    try {
      await api.delete(`/profit-rules/roles/${id}`);
      setRoles(roles.filter((r) => r._id !== id));
      setSuccess(t("admin.profitRulesSettings.success.roleDeleted"));
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.profitRulesSettings.errors.deleteRoleFailed"));
    } finally {
      setSaving(false);
    }
  };


  if (loading && contributors.length === 0 && roles.length === 0) {
    return (
      <Container>
        <Stack spacing="lg">
          <Spinner />
        </Stack>
      </Container>
    );
  }

  return (
    <Container>
      <Stack spacing="lg">
        <Heading size="xl">{t("admin.profitRulesSettings.title")}</Heading>
        <Text className="text-gray-600 dark:text-gray-400">
          {t("admin.profitRulesSettings.description")}
        </Text>

        {error && <Alert variant="error">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab("roles")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "roles"
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {t("admin.profitRulesSettings.tabs.roles")}
            </button>
            <button
              onClick={() => setActiveTab("contributors")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "contributors"
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {t("admin.profitRulesSettings.tabs.contributors")}
            </button>
            <button
              onClick={() => setActiveTab("category-rules")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "category-rules"
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {t("admin.profitRulesSettings.tabs.categoryRules")}
            </button>
          </nav>
        </div>

            {/* Roles Tab */}
            {activeTab === "roles" && (
              <Card padding="lg">
                <Stack spacing="md">
                  <Heading size="md">{t("admin.profitRulesSettings.roles.title")}</Heading>
                  <form onSubmit={handleCreateRole}>
                    <Stack spacing="sm">
                      <Input
                        label={t("admin.profitRulesSettings.roles.name")}
                        required
                        value={roleForm.name}
                        onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                      />
                      <Input
                        label={t("admin.profitRulesSettings.roles.description")}
                        value={roleForm.description}
                        onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                      />
                      <Button type="submit" disabled={saving}>
                        <FaPlus className="mr-2" />
                        {t("admin.profitRulesSettings.roles.add")}
                      </Button>
                    </Stack>
                  </form>

                  <div className="mt-6">
                    <Heading size="sm" className="mb-4">{t("admin.profitRulesSettings.roles.existing")}</Heading>
                    {roles.length === 0 ? (
                      <Text className="text-gray-500">{t("admin.profitRulesSettings.roles.none")}</Text>
                    ) : (
                      <div className="space-y-2">
                        {roles.map((role) => (
                          <div key={role._id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded">
                            {editingRole === role._id ? (
                              <div className="flex-1 flex gap-2">
                                <Input
                                  value={roleForm.name || role.name}
                                  onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                                  className="flex-1"
                                />
                                <Input
                                  value={roleForm.description || role.description || ""}
                                  onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                                  className="flex-1"
                                />
                                <Button
                                  size="sm"
                                  onClick={() => handleUpdateRole(role._id)}
                                  disabled={saving}
                                >
                                  {t("common.save")}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingRole(null);
                                    setRoleForm({ name: "", description: "" });
                                  }}
                                >
                                  {t("common.cancel")}
                                </Button>
                              </div>
                            ) : (
                              <>
                                <div>
                                  <Text className="font-medium">{role.name}</Text>
                                  {role.description && (
                                    <Text className="text-sm text-gray-500">{role.description}</Text>
                                  )}
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setEditingRole(role._id);
                                      setRoleForm({ name: role.name, description: role.description || "" });
                                    }}
                                  >
                                    <FaEdit />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDeleteRole(role._id)}
                                    disabled={saving}
                                  >
                                    <FaTrash />
                                  </Button>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Stack>
              </Card>
            )}

            {/* Contributors Tab */}
            {activeTab === "contributors" && (
          <Card padding="lg">
            <Stack spacing="md">
              <Heading size="md">{t("admin.profitRulesSettings.contributors.title")}</Heading>
              <form onSubmit={handleCreateContributor}>
                <Stack spacing="sm">
                  <Input
                    label={t("admin.profitRulesSettings.contributors.name")}
                    required
                    value={contributorForm.name}
                    onChange={(e) => setContributorForm({ ...contributorForm, name: e.target.value })}
                  />
                  <Input
                    label={t("admin.profitRulesSettings.contributors.email")}
                    type="email"
                    value={contributorForm.email}
                    onChange={(e) => setContributorForm({ ...contributorForm, email: e.target.value })}
                  />
                  <Button type="submit" disabled={saving}>
                    <FaPlus className="mr-2" />
                    {t("admin.profitRulesSettings.contributors.add")}
                  </Button>
                </Stack>
              </form>

              <div className="mt-6">
                <Heading size="sm" className="mb-4">{t("admin.profitRulesSettings.contributors.existing")}</Heading>
                {contributors.length === 0 ? (
                  <Text className="text-gray-500">{t("admin.profitRulesSettings.contributors.none")}</Text>
                ) : (
                  <div className="space-y-2">
                    {contributors.map((contributor) => (
                      <div
                        key={contributor._id}
                        className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded"
                      >
                        {editingContributor === contributor._id ? (
                          <div className="flex-1 flex gap-2">
                            <Input
                              value={contributorForm.name || contributor.name}
                              onChange={(e) => setContributorForm({ ...contributorForm, name: e.target.value })}
                              className="flex-1"
                            />
                            <Input
                              value={contributorForm.email || contributor.email || ""}
                              onChange={(e) => setContributorForm({ ...contributorForm, email: e.target.value })}
                              className="flex-1"
                              type="email"
                            />
                            <Button
                              size="sm"
                              onClick={() => handleUpdateContributor(contributor._id)}
                              disabled={saving}
                            >
                              {t("common.save")}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingContributor(null);
                                setContributorForm({ name: "", email: "" });
                              }}
                            >
                              {t("common.cancel")}
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div>
                              <Text className="font-medium">{contributor.name}</Text>
                              {contributor.email && (
                                <Text className="text-sm text-gray-500">{contributor.email}</Text>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setEditingContributor(contributor._id);
                                  setContributorForm({ name: contributor.name, email: contributor.email || "" });
                                }}
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteContributor(contributor._id)}
                                disabled={saving}
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Stack>
          </Card>
        )}

        {/* Category Rules Tab - Listing Only */}
        {activeTab === "category-rules" && (
          <Card padding="lg">
            <Stack spacing="md">
              <Heading size="md">{t("admin.profitRulesSettings.categoryRules.title")}</Heading>
              <Text className="text-gray-600 dark:text-gray-400">
                {t("admin.profitRulesSettings.categoryRules.listingDescription")}
              </Text>

              {loading ? (
                <Spinner />
              ) : categories.length === 0 ? (
                <Text className="text-gray-500">{t("admin.profitRulesSettings.categoryRules.noCategories")}</Text>
              ) : (
                <div className="space-y-4">
                  {categories.map((category) => {
                    const rules = categoryRules[category._id];
                    const rolePercentages = rules?.rolePercentages || {};
                    const percentages: Record<string, number> = {};
                    
                    // Convert Map to object if needed
                    if (rolePercentages instanceof Map) {
                      rolePercentages.forEach((value: number, key: string) => {
                        percentages[key] = value;
                      });
                    } else if (typeof rolePercentages === "object") {
                      Object.assign(percentages, rolePercentages);
                    }
                    
                    const totalPercentage = Object.values(percentages).reduce((sum, p) => sum + (p || 0), 0);
                    
                    return (
                      <div
                        key={category._id}
                        className="border border-gray-200 dark:border-gray-700 rounded p-4"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <Text className="font-medium text-lg">{category.name}</Text>
                          {Object.keys(percentages).length > 0 && (
                            <Text className="text-sm text-gray-500">
                              {t("admin.profitRulesSettings.categoryRules.total")}: {totalPercentage.toFixed(2)}%
                            </Text>
                          )}
                        </div>
                        {Object.keys(percentages).length === 0 ? (
                          <Text className="text-sm text-gray-500 italic">
                            {t("admin.profitRulesSettings.categoryRules.noRulesSet")}
                          </Text>
                        ) : (
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                            {Object.entries(percentages).map(([roleId, percentage]) => {
                              // Find role name
                              const role = roles.find((r: any) => r._id === roleId);
                              return (
                                <div key={roleId} className="text-sm">
                                  <Text className="font-medium">{role?.name || t("admin.profitRulesSettings.categoryRules.unknownRole")}: </Text>
                                  <Text>{percentage.toFixed(2)}%</Text>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Stack>
          </Card>
        )}
      </Stack>
    </Container>
  );
};

